import { useEffect, useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import type { PlantillaResumen, PreviaWhatsApp, WhatsAppCampana } from '@/lib/comunicaciones';
import BurbujaWhatsApp from './BurbujaWhatsApp';

/**
 * Elegir una plantilla aprobada, verla tal como llegará y mandarla a una
 * persona. Lo comparten el chat del lead y Conversaciones; quien lo usa decide
 * qué plantillas ofrece y a dónde se manda.
 *
 * {{1}} del cuerpo lleva su nombre de pila si se sabe; el resto de huecos (o
 * {{1}} si no hay nombre) se escriben aquí. Las plantillas con imagen en la
 * cabecera no se ofrecen: la imagen se elige en una campaña, no aquí.
 */
export default function ElegirPlantilla({ disponibles, nombrePila, idioma, onEnviar, ancho }: {
  disponibles: PlantillaResumen[];
  nombrePila: string;
  idioma: string;
  onEnviar: (plantilla: string, parametros: WhatsAppCampana['parametros']) => Promise<void>;
  /** En una ventana ancha: la lista a la izquierda y la vista previa a la derecha. */
  ancho?: boolean;
}) {
  const [elegida, setElegida] = useState(disponibles[0]?.nombre || '');
  const p = disponibles.find(x => x.nombre === elegida) || disponibles[0];
  const [textos, setTextos] = useState<Record<string, string>>({});
  const [previa, setPrevia] = useState<PreviaWhatsApp | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nombre = nombrePila.trim();
  const huecos = useMemo(() => (p?.huecos || []).filter(h => !h.imagen), [p]);
  const usaNombre = (clave: string) => clave === 'body.1' && !!nombre;
  const parametros: WhatsAppCampana['parametros'] = useMemo(() => Object.fromEntries(huecos.map(h => [
    h.clave,
    h.clave === 'body.1' && !!nombre ? { tipo: 'nombre' as const, valor: '' } : { tipo: 'texto' as const, valor: textos[h.clave] || '' },
  ])), [huecos, textos, nombre]);
  const listo = !!p && huecos.every(h => usaNombre(h.clave) || (textos[h.clave] || '').trim());

  useEffect(() => {
    if (!p) return;
    let vigente = true;
    const t = setTimeout(() => {
      api.vistaPreviaWhatsApp({ plantilla: p.nombre, parametros, cabeceraImagen: '' }, idioma || 'es', 'es', nombre || '…')
        .then(r => { if (vigente) setPrevia(r.previa); })
        .catch(() => { if (vigente) setPrevia(null); });
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  }, [p, parametros, idioma, nombre]);

  if (!p) return null;

  const enviar = async () => {
    setEnviando(true);
    setError(null);
    try {
      await onEnviar(p.nombre, parametros);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  const lista = (
    <div role="radiogroup" aria-label="Plantillas" className={classNames('space-y-1.5 overflow-auto', ancho ? 'max-h-[60vh]' : 'max-h-40')}>
      {disponibles.map(x => (
        <label key={x.nombre} className={classNames(
          'flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm',
          x.nombre === p.nombre ? 'border-sereno-dark bg-slate-50' : 'border-gray-200 hover:border-slate-300',
        )}>
          <input type="radio" name="plantilla" className="mt-0.5" checked={x.nombre === p.nombre}
            onChange={() => { setElegida(x.nombre); setTextos({}); setError(null); }} />
          <span className="min-w-0 flex-1">
            <span className="font-mono text-xs block truncate">{x.nombre}</span>
            <span className="text-[11px] text-sereno-dark/45">
              {x.uso === 'leads' ? 'para leads' : x.uso === 'portal' ? 'del portal' : 'de campañas'} · {x.idiomas.filter(i => i.estado === 'APPROVED').map(i => i.idioma).join(' · ')}
            </span>
          </span>
        </label>
      ))}
    </div>
  );

  const detalle = (
    <div className="space-y-2 min-w-0">
      {huecos.filter(h => !usaNombre(h.clave)).map(h => (
        <input
          key={h.clave}
          className="input text-sm"
          value={textos[h.clave] || ''}
          placeholder={h.clave === 'body.1' ? '{{1}}: su nombre (no lo sabemos)' : `{{${h.nombre}}} (${h.donde})`}
          onChange={e => setTextos(t => ({ ...t, [h.clave]: e.target.value }))}
        />
      ))}
      {previa && <div className={classNames('overflow-auto rounded-xl', ancho ? 'max-h-[50vh]' : 'max-h-56')}><BurbujaWhatsApp previa={previa} hora="ahora" /></div>}
      {error && <div className="text-xs text-red-600">{error}</div>}
      <button type="button" onClick={() => void enviar()} disabled={!listo || enviando} className="btn-primary w-full flex items-center justify-center gap-2 text-sm">
        {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar «{p.nombre}»
      </button>
    </div>
  );

  return ancho
    ? <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start">{lista}{detalle}</div>
    : <div className="space-y-2">{disponibles.length > 1 && lista}{detalle}</div>;
}

